import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { SearchableSelect } from "../components/common/SearchableSelect";

const inr = (v) => `\u20b9${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monthStart = () => { const d = new Date(); return ymd(new Date(d.getFullYear(), d.getMonth(), 1)); };
const num = (v) => Number(v || 0).toLocaleString("en-IN");
const today = () => ymd(new Date());

export default function DprFinancial() {
  const { ctx } = useWorkspace();
  const { has } = useAuth();
  const canFull = has("DPR.VIEW_FINANCIALS") && has("DPR.VIEW_COMMERCIALS");
  const pid = ctx.project?.id;
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [singleDay, setSingleDay] = useState(false);
  const [allProjects, setAllProjects] = useState(false);
  const [dataAll, setDataAll] = useState(null);
  const [day, setDay] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [marginSort, setMarginSort] = useState(""); // "" = default team order, "desc" = high→low, "asc" = low→high

  const effFrom = singleDay ? day : from;
  const effTo = singleDay ? day : to;

  const load = useCallback(() => {
    if (!effFrom || !effTo) return;
    if (allProjects) {
      setLoading(true);
      api.get("/dpr/financial/all-projects", { params: { date_from: effFrom, date_to: effTo } })
        .then((r) => setDataAll(r.data))
        .catch((e) => toast.error(e.response?.data?.detail || "Failed to load financials"))
        .finally(() => setLoading(false));
      return;
    }
    if (!pid) return;
    setLoading(true);
    api.get("/dpr/financial", { params: { project_id: pid, date_from: effFrom, date_to: effTo } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load financials"))
      .finally(() => setLoading(false));
  }, [pid, effFrom, effTo, allProjects]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    try {
      const res = await api.get("/dpr/financial/pdf", { params: { project_id: pid, date_from: effFrom, date_to: effTo }, responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = u; a.download = `DPR_Financial_${effFrom}${effTo !== effFrom ? `_${effTo}` : ""}.pdf`; a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Export failed"); }
  };

  if (!pid && !allProjects) return (<div><PageHeader title="DPR Financial" subtitle="Revenue, cost & margin by date range" /><ProjectScopeBanner /><p className="mt-6 text-sm text-slate-500" data-testid="dprfin-need-project">Select a Project (top bar) to view financials.</p></div>);

  const g = data?.grand || {};
  const summaryOnly = !!data?.summary_only;
  const teamsFiltered = (data?.teams || []).filter((t) => !typeFilter || t.team_type === typeFilter);
  const teamsView = marginSort && !summaryOnly
    ? [...teamsFiltered].sort((a, b) => marginSort === "desc" ? Number(b.margin) - Number(a.margin) : Number(a.margin) - Number(b.margin))
    : teamsFiltered;
  const cycleMarginSort = () => setMarginSort((s) => (s === "" ? "desc" : s === "desc" ? "asc" : ""));
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-x-4 gap-y-2 border-b pb-3" data-testid="dprfin-topbar">
        <div className="mr-2">
          <h1 className="font-display text-lg font-bold leading-tight tracking-tight text-slate-900">DPR Financial</h1>
          <div className="text-xs text-slate-500">{ctx.project ? `${ctx.project.project_code || ""} — ${ctx.project.project_name || ""}` : ""}</div>
        </div>
        <div className="flex overflow-hidden rounded-md border border-slate-300" data-testid="dprfin-mode-switch">
          <button type="button" data-testid="dprfin-mode-range" onClick={() => { setSingleDay(false); setAllProjects(false); }}
            className={`h-8 px-3 text-xs font-medium transition-colors ${!singleDay && !allProjects ? "bg-teal-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Date Range</button>
          <button type="button" data-testid="dprfin-mode-single" onClick={() => { setSingleDay(true); setAllProjects(false); }}
            className={`h-8 px-3 text-xs font-medium transition-colors ${singleDay && !allProjects ? "bg-teal-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Single Date</button>
          <button type="button" data-testid="dprfin-mode-allprojects" onClick={() => setAllProjects(true)}
            className={`h-8 px-3 text-xs font-medium transition-colors ${allProjects ? "bg-teal-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>All Projects</button>
        </div>
        {singleDay ? (
          <div><Label className="text-xs text-slate-500">Date</Label><Input data-testid="dprfin-day" type="date" value={day} onChange={(e) => setDay(e.target.value)} className="mt-1 h-8 w-40" /></div>
        ) : (<>
          <div><Label className="text-xs text-slate-500">From</Label><Input data-testid="dprfin-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-8 w-40" /></div>
          <div><Label className="text-xs text-slate-500">To</Label><Input data-testid="dprfin-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-8 w-40" /></div>
        </>)}
        <div className="flex gap-1.5">
          <Button data-testid="dprfin-preset-today" size="sm" variant="outline" className="h-8" onClick={() => { if (singleDay) setDay(today()); else { setFrom(today()); setTo(today()); } }}>Today</Button>
          <Button data-testid="dprfin-preset-month" size="sm" variant="outline" className="h-8" onClick={() => { setSingleDay(false); setFrom(monthStart()); setTo(today()); }}>This Month</Button>
        </div>
        <Button data-testid="dprfin-apply" size="sm" className="h-8 bg-teal-700 hover:bg-teal-800" onClick={load} disabled={loading}>{loading ? "Loading…" : "Apply"}</Button>
        {canFull && !allProjects && <Button data-testid="dprfin-pdf" size="sm" variant="outline" className="ml-auto h-8" onClick={downloadPdf}><FileText className="mr-1 h-4 w-4" />PDF</Button>}
      </div>

      {allProjects ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="dprfin-allprojects">
          {(dataAll?.projects || []).map((p) => (
            <div key={p.project_id} className="min-w-0 rounded-xl border bg-white p-4" data-testid={`dprfin-allproj-${p.project_id}`}>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-teal-700">{p.project_code ? `${p.project_code} — ` : ""}{p.project_name}</span>
                {!dataAll?.summary_only && <span className={`text-xs font-semibold ${Number(p.grand?.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>Margin {inr(p.grand?.margin)}</span>}
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead><tr className="border-b bg-slate-50 text-xs uppercase text-slate-500"><th className="px-3 py-2">Date</th>{!dataAll?.summary_only ? (<><th className="px-3 py-2 text-right">Revenue</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Margin</th></>) : <th className="px-3 py-2 text-right">Result</th>}</tr></thead>
                <tbody>
                  {(p.days || []).map((d) => (
                    <tr key={d.date} className="border-b last:border-0">
                      <td className="px-3 py-1.5">{d.date}</td>
                      {!dataAll?.summary_only ? (<>
                        <td className="px-3 py-1.5 text-right text-teal-700">{inr(d.revenue)}</td>
                        <td className="px-3 py-1.5 text-right text-slate-600">{inr(d.cost)}</td>
                        <td className={`px-3 py-1.5 text-right ${Number(d.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(d.margin)}</td>
                      </>) : (
                        <td className="px-3 py-1.5 text-right"><Badge className={d.status === "PROFIT" ? "bg-emerald-600" : "bg-red-500"}>{d.status === "PROFIT" ? "Profit" : "Loss"}</Badge></td>
                      )}
                    </tr>
                  ))}
                  {!(p.days || []).length && <tr><td colSpan={dataAll?.summary_only ? 2 : 4} className="px-3 py-3 text-slate-400">No DPR recorded in this range.</td></tr>}
                </tbody>
                {!dataAll?.summary_only && (p.days || []).length > 0 && (
                  <tfoot><tr className="border-t-2 bg-slate-50 text-xs font-bold"><td className="px-3 py-2">Total</td><td className="px-3 py-2 text-right text-teal-700">{inr(p.grand?.revenue)}</td><td className="px-3 py-2 text-right">{inr(p.grand?.cost)}</td><td className={`px-3 py-2 text-right ${Number(p.grand?.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(p.grand?.margin)}</td></tr></tfoot>
                )}
              </table>
              </div>
            </div>
          ))}
          {dataAll && !(dataAll.projects || []).length && <div className="text-sm text-slate-400">No projects in scope.</div>}
        </div>
      ) : (<>
      {summaryOnly ? (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="dprfin-grand">
          <div className="rounded-xl border bg-white p-3"><div className="text-xs uppercase text-slate-400">Total Activity</div><div className="text-xl font-bold text-slate-800">{num(g.activity_total)}</div></div>
          <div className="flex items-center rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="dprfin-restricted-note">Amounts are restricted for your role — you can view activity counts and team profit/loss status only.</div>
        </div>
      ) : (
      <div className="mb-4 grid grid-cols-3 gap-3" data-testid="dprfin-grand">
        <div className="rounded-xl border bg-white p-3"><div className="text-xs uppercase text-slate-400">Total Revenue</div><div className="text-xl font-bold text-teal-700">{inr(g.revenue)}</div></div>
        <div className="rounded-xl border bg-white p-3"><div className="text-xs uppercase text-slate-400">Total Cost</div><div className="text-xl font-bold text-slate-700">{inr(g.cost)}</div></div>
        <div className="rounded-xl border bg-white p-3"><div className="text-xs uppercase text-slate-400">Margin</div><div className={`text-xl font-bold ${Number(g.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(g.margin)}</div></div>
      </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="min-w-0 rounded-xl border bg-white p-4 lg:col-span-3" data-testid="dprfin-teams">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-teal-700">Team-wise Financials</span>
            <div className="w-36"><SearchableSelect testid="dprfin-type-filter" value={typeFilter} onChange={setTypeFilter} options={[{ value: "", label: "All types" }, { value: "VEHICLE", label: "Vehicle" }, { value: "BIKE", label: "Bike" }]} placeholder="All types" /></div>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b bg-slate-50 text-xs uppercase text-slate-500"><th className="px-3 py-2">Team</th><th className="px-3 py-2 min-w-[240px]">Members / Type</th><th className="px-3 py-2 text-right whitespace-nowrap">Total Activity</th>{!summaryOnly && (<><th className="px-3 py-2 text-right whitespace-nowrap">Revenue</th><th className="px-3 py-2 text-right whitespace-nowrap">Cost</th><th className="px-3 py-2 text-right whitespace-nowrap cursor-pointer select-none" data-testid="dprfin-margin-sort" title="Click: High → Low → Low → High → Reset" onClick={cycleMarginSort}><span className="inline-flex items-center gap-1">Margin{marginSort === "desc" ? <ArrowDown className="h-3.5 w-3.5 text-teal-700" /> : marginSort === "asc" ? <ArrowUp className="h-3.5 w-3.5 text-teal-700" /> : <ArrowUpDown className="h-3.5 w-3.5 text-slate-300" />}</span></th></>)}{summaryOnly && <th className="px-3 py-2 text-right whitespace-nowrap">Result</th>}</tr></thead>
            <tbody>
              {teamsView.map((t) => (
                <tr key={t.team_name} className="border-b last:border-0">
                  <td className="px-3 py-1.5 font-medium whitespace-nowrap align-top">{t.team_name}</td>
                  <td className="px-3 py-1.5 align-top min-w-[240px]">
                    <div className="flex items-start gap-1.5">
                      {t.team_type && <Badge className={`shrink-0 ${t.team_type === "VEHICLE" ? "bg-teal-600" : "bg-indigo-600"}`}>{t.team_type}</Badge>}
                      <span className="text-xs leading-snug text-slate-500">
                        {(t.members || []).map((m) => `${m.member_name}${m.designation ? ` (${m.designation.slice(0, 3)})` : ""}`).join(", ") || "—"}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-right font-semibold text-slate-800 align-top" data-testid={`dprfin-activity-${t.team_name}`}>{t.activity_total ?? 0}</td>
                  {!summaryOnly ? (<>
                    <td className="px-3 py-1.5 text-right text-teal-700 align-top">{inr(t.revenue)}</td>
                    <td className="px-3 py-1.5 text-right text-slate-600 align-top">{inr(t.cost)}</td>
                    <td className={`px-3 py-1.5 text-right align-top ${Number(t.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(t.margin)}</td>
                  </>) : (
                    <td className="px-3 py-1.5 text-right align-top"><Badge data-testid={`dprfin-status-${t.team_name}`} className={t.status === "PROFIT" ? "bg-emerald-600" : "bg-red-500"}>{t.status === "PROFIT" ? "Profit" : "Loss"}</Badge></td>
                  )}
                </tr>
              ))}
              {!teamsView.length && <tr><td colSpan={summaryOnly ? 4 : 6} className="px-3 py-3 text-slate-400">No financial data in this range.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>

        <div className="min-w-0 rounded-xl border bg-white p-4 lg:col-span-2" data-testid="dprfin-days">
          <div className="mb-2 text-sm font-semibold text-teal-700">Day-wise Financials</div>
          <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b bg-slate-50 text-xs uppercase text-slate-500"><th className="px-3 py-2">Date</th>{!summaryOnly ? (<><th className="px-3 py-2 text-right">Revenue</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Margin</th></>) : <th className="px-3 py-2 text-right">Result</th>}</tr></thead>
            <tbody>
              {(data?.days || []).map((d) => (
                <tr key={d.date} className="border-b last:border-0">
                  <td className="px-3 py-1.5">{d.date}</td>
                  {!summaryOnly ? (<>
                    <td className="px-3 py-1.5 text-right text-teal-700">{inr(d.revenue)}</td>
                    <td className="px-3 py-1.5 text-right text-slate-600">{inr(d.cost)}</td>
                    <td className={`px-3 py-1.5 text-right ${Number(d.margin) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(d.margin)}</td>
                  </>) : (
                    <td className="px-3 py-1.5 text-right"><Badge className={d.status === "PROFIT" ? "bg-emerald-600" : "bg-red-500"}>{d.status === "PROFIT" ? "Profit" : "Loss"}</Badge></td>
                  )}
                </tr>
              ))}
              {!(data?.days || []).length && <tr><td colSpan={summaryOnly ? 2 : 4} className="px-3 py-3 text-slate-400">No financial data in this range.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}
